
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Layout from "@/components/layout/Layout";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/components/ui/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { OptimizedImage } from "@/components/ui/image";
import { OptimizedCourseCard } from "@/components/courses/OptimizedCourseCard";
import { OptimizedBlogPostCard } from "@/components/blog/OptimizedBlogPostCard";

interface Course {
  id: string;
  title: string;
  description: string;
  level: string;
  duration: string;
  status: string;
  image_url: string | null;
  price: string;
}

interface BlogPost {
  id: string;
  title: string;
  excerpt: string;
  category: string;
  created_at: string;
  image_url: string | null;
  text_direction: "ltr" | "rtl";
}

const Profile = () => {
  const { user, userRoles } = useAuth();
  const [enrolledCourses, setEnrolledCourses] = useState<Course[]>([]);
  const [recentBlogs, setRecentBlogs] = useState<BlogPost[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const { toast } = useToast();

  useEffect(() => {
    const fetchUserData = async () => {
      if (!user) return;
      
      setIsLoading(true);
      try {
        // Fetch enrolled courses
        const { data: enrollments, error: enrollmentsError } = await supabase
          .from('course_enrollments')
          .select('course_id')
          .eq('user_id', user.id);
          
        if (enrollmentsError) throw enrollmentsError;
        
        if (enrollments && enrollments.length > 0) {
          const courseIds = enrollments.map(enrollment => enrollment.course_id);
          
          const { data: coursesData, error: coursesError } = await supabase
            .from('courses')
            .select('id, title, description, level, duration, status, image_url, price')
            .in('id', courseIds);
            
          if (coursesError) throw coursesError;
          setEnrolledCourses(coursesData || []);
        }
        
        // Fetch recent blog posts
        const { data: blogs, error: blogsError } = await supabase
          .from('blog_posts')
          .select('id, title, excerpt, category, created_at, image_url, text_direction')
          .order('created_at', { ascending: false })
          .limit(5);
          
        if (blogsError) throw blogsError;
        
        const typedBlogs = blogs?.map(blog => ({
          ...blog,
          text_direction: (blog.text_direction as "ltr" | "rtl") || "ltr"
        }));
        
        setRecentBlogs(typedBlogs || []);
        
      } catch (error: any) {
        console.error("Error fetching user data:", error.message);
        toast({
          title: "Error loading profile data",
          description: error.message,
          variant: "destructive"
        });
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchUserData();
  }, [user, toast]);
  
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };
  
  const getInitials = (user: any) => {
    if (!user) return "U";
    
    const firstName = user.user_metadata?.first_name || "";
    const lastName = user.user_metadata?.last_name || "";
    
    if (firstName && lastName) {
      return `${firstName.charAt(0)}${lastName.charAt(0)}`;
    } else if (firstName) {
      return firstName.charAt(0);
    } else if (user.email) {
      return user.email.charAt(0).toUpperCase();
    }
    
    return "U";
  };

  if (!user) {
    return (
      <Layout>
        <div className="flex justify-center items-center h-[60vh]">
          <Card className="w-full max-w-md">
            <CardHeader>
              <CardTitle>Please Sign In</CardTitle>
              <CardDescription>
                You need to be logged in to view your profile.
              </CardDescription>
            </CardHeader>
            <CardFooter>
              <Link to="/login" className="w-full">
                <Button className="w-full">Go to Login</Button>
              </Link>
            </CardFooter>
          </Card>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="mb-12">
          <h1 className="text-4xl font-bold mb-8">
            <span className="text-[#34BF49]">My Profile</span>
          </h1>
          
          <Card className="mb-10">
            <CardContent className="pt-6">
              <div className="flex flex-col md:flex-row items-center md:items-start gap-6">
                <Avatar className="h-24 w-24">
                  <AvatarImage src={user.user_metadata?.avatar_url || ""} alt={user.email || "User"} />
                  <AvatarFallback className="text-2xl">{getInitials(user)}</AvatarFallback>
                </Avatar>
                
                <div className="space-y-3 text-center md:text-left">
                  <h2 className="text-2xl font-bold">
                    {user.user_metadata?.first_name || ""} {user.user_metadata?.last_name || ""}
                    {!user.user_metadata?.first_name && !user.user_metadata?.last_name && user.email}
                  </h2>
                  <p className="text-muted-foreground">{user.email}</p>
                  
                  <div className="flex flex-wrap gap-2 justify-center md:justify-start">
                    {userRoles.map((role) => (
                      <Badge key={role} variant="outline" className="capitalize">
                        {role}
                      </Badge>
                    ))}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Tabs defaultValue="courses">
            <TabsList className="mb-6">
              <TabsTrigger value="courses">My Courses</TabsTrigger>
              <TabsTrigger value="blogs">Recent Blogs</TabsTrigger>
            </TabsList>
            
            <TabsContent value="courses" className="space-y-6">
              <h2 className="text-2xl font-semibold mb-6">Enrolled Courses</h2>
              
              {isLoading ? (
                <div className="flex justify-center py-12">
                  <div className="animate-pulse">Loading courses...</div>
                </div>
              ) : enrolledCourses.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {enrolledCourses.map((course) => (
                    <OptimizedCourseCard
                      key={course.id}
                      id={course.id}
                      title={course.title}
                      level={course.level}
                      price={course.price || "Free"}
                      status={course.status}
                      image_url={course.image_url}
                      enrolled={true}
                    />
                  ))}
                </div>
              ) : (
                <div className="bg-muted/50 rounded-lg p-8 text-center">
                  <h3 className="text-lg font-medium mb-3">No Enrolled Courses Yet</h3>
                  <p className="text-muted-foreground mb-6">Explore our courses and start your learning journey today.</p>
                  <Link to="/courses">
                    <Button>Browse Courses</Button>
                  </Link>
                </div>
              )}
            </TabsContent>
            
            <TabsContent value="blogs" className="space-y-6">
              <h2 className="text-2xl font-semibold mb-6">Recent Blog Posts</h2>
              
              {isLoading ? (
                <div className="flex justify-center py-12">
                  <div className="animate-pulse">Loading blog posts...</div>
                </div>
              ) : recentBlogs.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {recentBlogs.map((blog) => (
                    <OptimizedBlogPostCard
                      key={blog.id}
                      id={blog.id}
                      title={blog.title}
                      excerpt={blog.excerpt}
                      category={blog.category}
                      image_url={blog.image_url}
                      text_direction={blog.text_direction}
                    />
                  ))}
                </div>
              ) : (
                <div className="bg-muted/50 rounded-lg p-8 text-center">
                  <h3 className="text-lg font-medium mb-3">No Blog Posts Available</h3>
                  <p className="text-muted-foreground mb-6">Check back later for new content or browse our existing articles.</p>
                  <Link to="/blog">
                    <Button>Browse Blog</Button>
                  </Link>
                </div>
              )}
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </Layout>
  );
};

export default Profile;
